// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 17.9.0 — Floating tour controller overlay bar.
 * Shows play/pause, speed, progress, mode toggle, and exit button.
 */

import { createPortal } from 'react-dom';
import { useEffect, useRef } from 'react';
import type { TourMode, TourSpeed, TourEngineStatus } from '../hooks/useTourEngine.js';
import { useLocale } from '../hooks/useLocale.js';
import { formatTemplate } from '../utils/i18n-helpers.js';

interface TourControllerProps {
  status: TourEngineStatus;
  currentStep: number;
  totalSteps: number;
  mode: TourMode;
  speed: TourSpeed;
  tourTitle: string;
  onPlayPause: () => void;
  onNext: () => void;
  onPrev: () => void;
  onSpeedChange: (speed: TourSpeed) => void;
  onModeChange: (mode: TourMode) => void;
  onExit: () => void;
}

const SPEEDS: TourSpeed[] = [0.5, 1, 2];

export function TourController({
  status,
  currentStep,
  totalSteps,
  mode,
  speed,
  tourTitle,
  onPlayPause,
  onNext,
  onPrev,
  onSpeedChange,
  onModeChange,
  onExit,
}: TourControllerProps) {
  const { t } = useLocale();
  const isPaused = status === 'paused';
  // WCAG 2.5.3 — the accessible name is BUILT from the visible label (the
  // current mode) and the target mode, both read from the same keys the button
  // shows, so the two cannot drift in any locale (fix-sprint-20261025-2133 F8).
  const currentModeLabel =
    mode === 'autoplay' ? t.onboarding.tours.autoplay : t.onboarding.tours.interactive;
  const otherModeLabel =
    mode === 'autoplay' ? t.onboarding.tours.interactive : t.onboarding.tours.autoplay;
  const modeToggleName = formatTemplate(t.onboarding.tours.mode_switch_name, {
    current: currentModeLabel,
    other: otherModeLabel,
  });
  const progressPct = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;
  const toolbarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    toolbarRef.current
      ?.querySelector<HTMLElement>('button:not(:disabled)')
      ?.focus({ preventScroll: true });
  }, []);

  // Render into document.body via portal so the controller escapes any
  // ancestor stacking contexts and truly sits above driver.js's overlay.
  return createPortal(
    <div
      className="grc-tour-controller"
      role="toolbar"
      aria-label={tourTitle}
      data-tour-controls
      ref={toolbarRef}
    >
      {/* Progress bar */}
      <div className="grc-tour-controller__progress">
        <div className="grc-tour-controller__progress-fill" style={{ width: `${progressPct}%` }} />
      </div>

      <div className="grc-tour-controller__body">
        {/* Tour title */}
        <span className="grc-tour-controller__title">{tourTitle}</span>

        {/* Step counter */}
        <span className="grc-tour-controller__step-count">
          {t.onboarding.tours.step_of
            .replace('{current}', String(currentStep + 1))
            .replace('{total}', String(totalSteps))}
        </span>

        {/* Navigation buttons */}
        <div className="grc-tour-controller__nav">
          <button
            className="grc-tour-controller__btn"
            onClick={onPrev}
            disabled={currentStep <= 0}
            title={t.common.previous || 'Previous'}
            aria-label={t.onboarding.tours.previous}
          >
            ‹
          </button>

          {mode === 'autoplay' && (
            <button
              className="grc-tour-controller__btn grc-tour-controller__btn--play"
              onClick={onPlayPause}
              title={isPaused ? t.common.resume || 'Resume' : t.common.pause || 'Pause'}
              aria-label={isPaused ? t.onboarding.tours.resume : t.onboarding.tours.pause}
            >
              {isPaused ? '▶' : '⏸'}
            </button>
          )}

          <button
            className="grc-tour-controller__btn"
            onClick={onNext}
            disabled={mode === 'autoplay' && currentStep >= totalSteps - 1}
            title={t.onboarding.tours.next}
            aria-label={t.onboarding.tours.next}
          >
            ›
          </button>
        </div>

        {/* Speed selector (auto-play only) */}
        {mode === 'autoplay' && (
          <div className="grc-tour-controller__speed">
            {SPEEDS.map((s) => (
              <button
                key={s}
                className={`grc-tour-controller__speed-btn ${speed === s ? 'grc-tour-controller__speed-btn--active' : ''}`}
                onClick={() => onSpeedChange(s)}
                aria-label={`${t.onboarding.tours.speed} ${s}x`}
                aria-pressed={speed === s}
              >
                {s}x
              </button>
            ))}
          </div>
        )}

        {/* Mode toggle */}
        <button
          className="grc-tour-controller__btn grc-tour-controller__btn--mode"
          onClick={() => onModeChange(mode === 'autoplay' ? 'interactive' : 'autoplay')}
          /* WCAG 2.5.3 / 4.1.2 — the accessible name starts with the visible
             label (the current mode) and then names the action. */
          title={modeToggleName}
          aria-label={modeToggleName}
        >
          {mode === 'autoplay' ? '⚡' : '👆'}
          <span className="grc-tour-controller__mode-label">{currentModeLabel}</span>
        </button>

        {/* Exit button */}
        <button
          className="grc-tour-controller__btn grc-tour-controller__btn--exit"
          onClick={onExit}
          title={t.onboarding.tours.exit}
          aria-label={t.onboarding.tours.exit}
        >
          ✕
        </button>
      </div>
    </div>,
    document.body,
  );
}
